'use client'

import React from 'react'
import { useContext } from 'react'
import { Session } from 'next-auth'
import { signIn } from 'next-auth/react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Button, Navbar, Dropdown } from 'flowbite-react'
import type { CustomFlowbiteTheme } from 'flowbite-react'
import { HiGift, HiQuestionMarkCircle } from 'react-icons/hi'
import { MobileDrawer } from '@tribex/stackup-react-ui'

import NavBarContext from '@/app/web/context/NavContext'
import { AppLogo } from '@/app/web/ui'
import MenuIcon from '@/public/images/icons/menu.svg'
import UserIcon from '@/public/images/icons/user.svg'
import { navigate } from '@/utils/Redirect'

const customTheme: CustomFlowbiteTheme['navbar'] = {
  root: {
    base: 'bg-white dark:border-gray-700 dark:bg-gray-800 sticky top-0 z-40 px-5 md:px-11 py-2.5 md:py-3.5 border-b border-gray-200',
    inner: {
      base: 'flex items-center gap',
    },
  },
  brand: {
    base: 'flex items-center gap-6 ml-4 md:ml-0',
  },
  collapse: {
    base: 'w-full md:block md:w-auto ml-4 md:ml-10',
    list: 'mt-4 flex flex-col md:mt-0 md:flex-row md:space-x-4 md:text-sm md:font-medium',
  },
  link: {
    base: 'block p-2',
    active: {
      on: 'bg-gray-200 text-white dark:text-white text-gray-700 rounded-xl',
      off: 'border-b border-gray-100 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white md:border-0 md:hover:bg-transparent md:hover:text-gray-300 md:dark:hover:bg-transparent md:dark:hover:text-white',
    },
  },
  toggle: {
    base: 'inline-flex items-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600',
  },
}

interface AppTopNavProps {
  session?: Session | null
}

const AppTopNav: React.FC<AppTopNavProps> = ({ session }) => {
  const { setIsMobile } = useContext(NavBarContext)
  const pathname = usePathname()
  const handleSignIn = () => {
    signIn('stackup', {
      callbackUrl: window.location.href,
    })
  }
  const homeUrl = process.env.NEXT_PUBLIC_HACKATHON_URL
  const listingEventUrl = pathname.startsWith('/web/bug-bounty')
    ? '/web/bug-bounty'
    : '/web/events'

  const MenuImg = (
    <Image
      src={MenuIcon}
      className="text-gray-500 dark:text-gray-500"
      alt="Menu"
      style={{
        maxWidth: '100%',
        height: 'auto',
      }}
    />
  )

  const handleGetStarted = () => {
    // Store the current URL to return to after signup and login
    const currentUrl = window.location.href
    localStorage.setItem('signup_return_url', currentUrl)

    // Redirect to our callback page which will trigger OAuth login
    // Include final_url as a parameter in the redirect_url itself
    const finalUrl = encodeURIComponent(currentUrl)
    const callbackUrl = `${window.location.origin}/auth/rails-callback?final_url=${finalUrl}`
    const registerUrl = `${process.env.NEXT_PUBLIC_STACKUP_HOST}/web/create-account?redirect_url=${encodeURIComponent(callbackUrl)}`
    window.location.href = registerUrl
  }

  const NoSessionNavItems = () => {
    return (
      <div className="flex md:order-last gap-6 ml-auto">
        <Button
          onClick={handleSignIn}
          color="transparent"
          className="hidden md:flex"
        >
          Log In
        </Button>
        <Button color="dark" onClick={handleGetStarted}>
          Get started
        </Button>
        <div className="flex md:hidden">
          <Dropdown
            arrowIcon={false}
            inline
            label={MenuImg}
            className="w-full md:max-w-xs"
          >
            <Dropdown.Item as={Link} href={listingEventUrl}>
              Explore
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => handleSignIn()}>Login</Dropdown.Item>
          </Dropdown>
        </div>
      </div>
    )
  }

  const SessionNavItems = () => {
    return (
      <div className="flex md:order-last gap-6 ml-auto">
        <Dropdown
          arrowIcon={false}
          inline
          label={MenuImg}
          className="w-full md:max-w-xs"
        >
          <Dropdown.Header
            onClick={() => {
              navigate(`/web/my/profile`)
            }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <Image
              src={session?.user.image ?? UserIcon}
              alt="User Settings"
              width={40}
              height={40}
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
            <span className="block text-sm">@{session?.user.username}</span>
          </Dropdown.Header>
          <Dropdown.Item
            as={Link}
            href="/web/my/rewards"
            icon={HiGift}
            className="gap-3"
          >
            Rewards
          </Dropdown.Item>
          <Dropdown.Item
            as={Link}
            href={listingEventUrl}
            className="flex md:hidden"
          >
            Explore
          </Dropdown.Item>
          <Dropdown.Divider className="flex md:hidden" />
          <Dropdown.Item icon={HiQuestionMarkCircle} className="gap-3">
            Help Center
          </Dropdown.Item>
        </Dropdown>
      </div>
    )
  }

  return (
    <Navbar theme={customTheme} fluid>
      <MobileDrawer
        onClick={() => setIsMobile(true)}
        className="border-r border-gray-200 -ml-2 pr-4 pb-1"
      />

      <Navbar.Brand href={homeUrl}>
        <AppLogo />
      </Navbar.Brand>
      {session ? <SessionNavItems /> : <NoSessionNavItems />}
      <Navbar.Collapse>
        <Navbar.Link
          href={listingEventUrl}
          className="font-semibold text-gray-900 hover:border hover:rounded-2xl md:hover:text-gray-900 md:active:bg-gray-200 md:active:text-gray-900 px-4"
        >
          Explore
        </Navbar.Link>
      </Navbar.Collapse>
    </Navbar>
  )
}

export default AppTopNav
